import React from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';

import {Button, Panel} from '@stubhub/bamboo';
import {loadingMessageText, seeMoreEventsText} from '@stubhub/react-event-helper';
import StoreProvider, {ssr_wait} from '@stubhub/react-store-provider';

import {updateEventCurrenciesIfNeeded} from '../../utils/eventUtils';
import EventList from '../react-event-list';

import './index.scss';

class EventListPlaceholder extends React.Component {
  render() {
    const _ = 'EventListPlaceholder';
    const container = cs(_);
    const eventsHeader = cs(`${_}__eventsHeader`);
    const eventsCell = cs(`${_}__eventsCell`);

    return (
      <div className={container}>
        <div className={eventsHeader} />
        <div className={eventsCell} />
        <div className={eventsCell} />
        <div className={eventsCell} />
        <div className={eventsCell} />
        <div className={eventsCell} />
        <div className={eventsCell} />
        <div className={eventsCell} />
      </div>
    );
  }
}

export class EventListPanel extends React.Component {
  /**
   * Method to load event from the Search Catalog Event API
   * @param props [object] The object containing the `props` from the component.
   * @param reset [boolean] The flag denoting if the currently loaded events have to reset and load the event from beginning.
   *
   */
  loadEvents(props, reset) {
    const {globalRegistry} = this.context;
    const {loadEvents} = this.props;
    props.globalRegistry = globalRegistry;
    // Sometimes when the props change and sort is empty, it does not send a value to the Search Catalog Event API
    const {searchParams = {}} = props;
    const {sort: spSort = ''} = searchParams;
    if (props.sort === '') {
      props.sort = spSort === '' ? 'eventDateLocal asc' : spSort;
    }

    return loadEvents(props, reset);
  }

  /**
   * Method called to initialize the events (while rendering the component in the server)
   * @param props [object] The object containing the `props` from the component.
   *
   */
  @ssr_wait
  init(props) {
    const {defaultSort} = this.props;

    return this.loadEvents({...props, events: [], ...this.getSortValue(defaultSort)}, true);
  }

  /**
   * Callback Method used by the sort filter to trigger the call to Search Catalog Event API to fetch next page of events.
   *
   */
  loadMore() {
    const props = {...this.props, ...this.getSortValue()};
    this.loadEvents(props);
  }

  /**
   * Method used to the Sort Value based on the selected sort key.
   * @param sortKey [number] The key associated to the selected sort option.
   *
   */
  getSortValue(sortKey) {
    const {sortOptions, defaultSort, sort} = this.props;
    sortKey = sortKey || sort || defaultSort;
    let value;
    sortOptions &&
      sortOptions.some((option) => {
        if (option.key === sortKey) {
          ({value} = option);

          return true;
        }

        return false;
      });

    return value;
  }

  /**
   * React Lifecycle Methods
   *
   */
  UNSAFE_componentWillMount() {
    const {searchParams, localStateId, id, events} = this.props;

    /**
     * Re-initialize if the `events` are empty or if `localStateId` from redux state doesn't match the New `id` passed in props
     * NOTE - Section Handles the Route change while component is unmounted.
     *
     */
    if ((!events || !events.length || (id && localStateId !== id)) && searchParams) {
      this.init(this.props);
    }
  }

  componentDidMount() {
    const {loading, events, updateEventCurrencies} = this.props;
    if (updateEventCurrencies && !loading && events && events.length) {
      updateEventCurrencies(events);
    }
  }

  componentDidUpdate(prevProps) {
    updateEventCurrenciesIfNeeded(this.props, prevProps, this.context);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    /**
     * Re-initialize if the current id is not the same a nextProps.id (Route change)
     *
     */
    const {searchParams: nextSearchParams = {}} = nextProps;
    const {sort: nextSort = 'eventDateLocal asc,distance asc,popularity desc', eventType: nextEventType = ''} =
      nextSearchParams;
    const {searchParams = {}, id, displayType} = this.props;
    const {sort = 'eventDateLocal asc,distance asc,popularity desc', eventType = ''} = searchParams;
    if (
      id !== nextProps.id ||
      displayType !== nextProps.displayType ||
      sort !== nextSort ||
      eventType !== nextEventType ||
      nextProps.reset === true
    ) {
      return this.loadEvents({...nextProps, events: []}, true);
    }
  }

  componentWillUnmount() {
    const {resetEvents} = this.props;
    typeof resetEvents === 'function' && resetEvents();
  }

  render() {
    const {
      numFound = 0,
      events = [],
      loading = false,
      hasMore,
      showDistance,
      eventItemDisplayOptions,
      disablePagination,
      className,
      hideBorder,
      hidePanelHeader = false,
      searchParams = {},
      entityType,
    } = this.props;
    let {title} = this.props;
    const {eventType} = searchParams;
    const firstLoading = numFound === 0 && loading;

    /**
     * If title is passed as a function then execute it with current props before using the Title
     *
     */
    title = typeof title === 'function' ? title(this.props) : title;

    /**
     * Header Section
     *
     */
    const header =
      title && !hidePanelHeader ? (
        <div className="EventListPanel__Header">
          <div className="EventListPanel__HeaderText">{title}</div>
        </div>
      ) : null;

    /**
     * Footer Section
     *
     */
    let footer = null;
    if (!disablePagination && hasMore) {
      footer = (
        <div className="EventListPanel__SeeMore">
          {loading ? (
            <Button appearance="secondary">{loadingMessageText}</Button>
          ) : (
            <Button appearance="secondary" onClick={this.loadMore.bind(this)}>
              {seeMoreEventsText}
            </Button>
          )}
        </div>
      );
    }

    const eventList = (
      <EventList
        className="EventResult"
        events={events}
        eventType={eventType}
        threshold={100}
        showDistance={showDistance}
        eventItemDisplayOptions={eventItemDisplayOptions}
        entityType={entityType}
      />
    );

    return !numFound ? (
      loading ? (
        <EventListPlaceholder />
      ) : null
    ) : firstLoading ? (
      loadingMessageText
    ) : (
      <Panel
        className={cs(className || 'EventListPanel')}
        footer={footer}
        header={header}
        content={eventList}
        border={!hideBorder}
      />
    );
  }
}

EventListPanel.contextTypes = {...StoreProvider.childContextTypes};

EventListPanel.propTypes = {
  /**
   * Event ID
   */
  id: PropTypes.any.isRequired,

  /**
   * Local state event ID
   */
  localStateId: PropTypes.any,

  /**
   * List of events
   */
  events: PropTypes.array,

  /**
   * If is loading
   */
  loading: PropTypes.bool,

  /**
   * Default sort criteria
   */
  defaultSort: PropTypes.string,

  /**
   * Sort options
   */
  sortOptions: PropTypes.array,

  /**
   * Method to reset events
   */
  resetEvents: PropTypes.func.isRequired,

  /**
   * Options Containing the Parts of the Event Items to be Hidden
   *
   */
  eventItemDisplayOptions: PropTypes.object,

  /**
   * Flag to Disable the use of location to get the events
   *
   */
  // eslint-disable-next-line react/no-unused-prop-types
  disableGeoLocation: PropTypes.bool,

  /**
   * Flag to Disable the Pagination for the component
   *
   */
  disablePagination: PropTypes.bool,

  /**
   * Class Name for Over-riding The Styles
   *
   */
  className: PropTypes.string,

  /**
   * Hide the Border around the Event Panel
   *
   */
  hideBorder: PropTypes.bool,

  // eslint-disable-next-line react/no-unused-prop-types
  ssr: PropTypes.bool,

  /**
   * Total number of events
   */
  numFound: PropTypes.number,

  /**
   * If there are more events to show
   */
  hasMore: PropTypes.bool,

  /**
   * If distance can be shown
   */
  showDistance: PropTypes.bool,

  /**
   * Indicate whether the event count panel header should display in event list
   */
  hidePanelHeader: PropTypes.bool,
  searchParams: PropTypes.object,

  /**
   * Method to get title or string
   */
  title: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),

  /**
   * Sort criteria
   */
  sort: PropTypes.string,

  /**
   * Method to update currency
   */
  updateEventCurrencies: PropTypes.func.isRequired,

  /**
   * Type
   */
  displayType: PropTypes.string,

  /**
   * Method to load events
   */
  loadEvents: PropTypes.func.isRequired,

  /** Type of entity. Can be any entity type or search */
  entityType: PropTypes.string,
};

EventListPanel.defaultProps = {
  ssr: true,
  disablePagination: false,
};

export default EventListPanel;
